import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi, tenantApi, type AdminUser, type Tenant } from "@/lib/api.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";

function safeJson(s: string): Record<string, string> {
  try { return JSON.parse(s) as Record<string, string>; } catch { return {}; }
}

function EmailVerifiedBadge({ verifiedAt }: { verifiedAt: string | null }) {
  if (verifiedAt) {
    return <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">인증됨</span>;
  }
  return <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-600">미인증</span>;
}

function AdminTenantPanel({ admin, allTenants }: { admin: AdminUser; allTenants: Tenant[] }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);

  const { data: assigned = [], isLoading } = useQuery({
    queryKey: ["admin-tenants", admin.id],
    queryFn: () => adminApi.getTenants(admin.id),
  });

  useEffect(() => {
    setSelected(new Set(assigned.map((t) => t.id)));
    setDirty(false);
  }, [assigned]);

  function toggle(tenantId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });
    setDirty(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const currentIds = new Set(assigned.map((t) => t.id));
      const toAdd = [...selected].filter((id) => !currentIds.has(id));
      const toRemove = [...currentIds].filter((id) => !selected.has(id));
      await Promise.all([
        ...toAdd.map((id) => adminApi.addTenant(admin.id, id)),
        ...toRemove.map((id) => adminApi.removeTenant(admin.id, id)),
      ]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tenants", admin.id] });
      setDirty(false);
    },
    onError: (err) => alert(err instanceof Error ? err.message : "저장에 실패했습니다."),
  });

  return (
    <div className="border-t border-gray-100 bg-gray-50 px-4 py-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500">사찰 배정</p>
        {dirty && (
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {saveMutation.isPending ? "저장 중..." : "저장"}
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="py-2 text-xs text-gray-400">불러오는 중...</p>
      ) : allTenants.length === 0 ? (
        <p className="py-2 text-xs text-gray-400">등록된 사찰이 없습니다.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {allTenants.map((t) => {
            const name = safeJson(t.name).ko || t.slug;
            const checked = selected.has(t.id);
            return (
              <label
                key={t.id}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                  checked ? "border-brand-300 bg-brand-50" : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(t.id)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-800">{name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AdminsPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: admins, isLoading } = useQuery({
    queryKey: ["admins"],
    queryFn: () => adminApi.list(),
  });

  const { data: allTenants = [] } = useQuery({
    queryKey: ["tenants"],
    queryFn: () => tenantApi.list("active"),
  });

  const createMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      adminApi.create(email, password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admins"] });
      setShowForm(false);
      setNewEmail("");
      setNewPassword("");
      setFormError(null);
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : "생성에 실패했습니다."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admins"] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "삭제에 실패했습니다.");
      setDeleteTarget(null);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) => adminApi.resetPassword(id),
    onSuccess: () => alert("비밀번호 재설정 이메일을 발송했습니다."),
    onError: (err) => alert(err instanceof Error ? err.message : "이메일 발송에 실패했습니다."),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!newEmail.trim() || !newPassword.trim()) {
      setFormError("이메일과 비밀번호를 모두 입력해 주세요.");
      return;
    }
    createMutation.mutate({ email: newEmail.trim(), password: newPassword });
  }

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">관리자 목록</h1>
        <button
          type="button"
          onClick={() => { setShowForm((v) => !v); setFormError(null); }}
          className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
        >
          {showForm ? "취소" : "+ 관리자 추가"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-semibold text-gray-700">새 관리자 추가</h2>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">이메일</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">초기 비밀번호 (8자 이상)</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                minLength={8}
                required
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="shrink-0 rounded-xl bg-brand-500 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {createMutation.isPending ? "생성 중..." : "생성"}
            </button>
          </div>
          {formError && <p className="mt-2 text-xs text-red-500">{formError}</p>}
        </form>
      )}

      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-400">불러오는 중...</div>
        ) : !admins?.length ? (
          <div className="p-12 text-center">
            <p className="text-gray-400">등록된 관리자가 없습니다.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {admins.map((admin) => (
              <div key={admin.id}>
                <div className="flex items-center gap-3 px-4 py-4 hover:bg-gray-50/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{admin.email}</p>
                    <p className="text-xs text-gray-400">{new Date(admin.created_at).toLocaleDateString("ko-KR")}</p>
                  </div>
                  <EmailVerifiedBadge verifiedAt={admin.email_verified_at} />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setExpandedId(expandedId === admin.id ? null : admin.id)}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    >
                      사찰 배정 {expandedId === admin.id ? "▲" : "▼"}
                    </button>
                    <button
                      onClick={() => resetPasswordMutation.mutate(admin.id)}
                      disabled={resetPasswordMutation.isPending}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                      비밀번호 재설정
                    </button>
                    <button
                      onClick={() => setDeleteTarget(admin)}
                      className="rounded-lg px-2 py-1.5 text-xs text-red-400 hover:bg-red-50 hover:text-red-600"
                    >
                      삭제
                    </button>
                  </div>
                </div>
                {expandedId === admin.id && (
                  <AdminTenantPanel admin={admin} allTenants={allTenants} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="관리자 삭제"
        description={`"${deleteTarget?.email ?? ""}" 관리자를 삭제할까요? 이 작업은 되돌릴 수 없습니다.`}
        confirmLabel="삭제"
        danger
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
